import { OnaWordmark } from "@/assets/icons/OnaWordmark";
import { PREDEFINED_TASKS, type PredefinedTask } from "@/components/ai/ai-constants";
import { OnaVerificationDialog } from "@/components/ai/OnaVerificationDialog";
import { NewPromptInput, type PromptInputSubmission } from "@/components/ai/prompt-input/NewPromptInput";
import {
    isLoadingAtom,
    usePromptAtom,
    usePendingSubmission,
    selectedContextsAtom,
    selectedEnvironmentClassAtom,
    contextAssociatedProjectsAtom,
    useAutoSavePromptInputState,
    useRestoreStoredPromptInputState,
} from "@/components/ai/prompt-input/state";
import { useSubmitPrompts } from "@/components/ai/prompt-input/use-submit-prompt";
import { RecentAgentExecutions } from "@/components/ai/RecentAgentExecutions";
import { type TaskCard } from "@/components/ai/TaskCard";
import { PromptTemplate } from "@/components/flexkit/PromptTemplate";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { useDynamicTitle } from "@/hooks/use-dynamic-title";
import { useFlagOnaAIEnabled, useFlagShareableOnaLinksEnabled } from "@/hooks/use-flag";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useStripePaymentRedirectResult } from "@/hooks/use-stripe-payment-redirect-result";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { formatError } from "@/utils/errors";
import { Code, ConnectError } from "@connectrpc/connect";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { PaymentMethodStatus } from "gitpod-next-api/gitpod/v1/billing_pb";
import { atom, useAtom, useAtomValue, useSetAtom, useStore } from "jotai";
import { type FC, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    clearOnboardingStateFromLocalStorage,
    clearPendingSubmissionFromLocalStorage,
    loadPendingSubmissionFromLocalStorage,
    savePendingSubmissionToLocalStorage,
} from "@/routes/ona-onboarding/local-storage";
import { useOrganization } from "@/queries/organization-queries";
import { useParseContextURL } from "@/queries/runner-queries";
import { useListProjects, type PlainProject } from "@/queries/project-queries";
import { useEnvironmentClassMap } from "@/hooks/use-environment-class-entries";
import { isValidURL } from "@/utils/url-validation";
import { useDebounce } from "use-debounce";
import { useSearchParams } from "react-router-dom";

const isVerificationDialogOpenAtom = atom(false);
const isPrePopulatedPromptAtom = atom(false);

export const OnaAIPage: FC = () => {
    useDynamicTitle("Ona");

    const location = useLocation();

    const contentRef = useRef<HTMLDivElement>(null);
    const { value: isOnaEnabled, loading: isLoadingOnaEnabled } = useFlagOnaAIEnabled();
    const { value: isShareableLinksEnabled } = useFlagShareableOnaLinksEnabled();
    const setIsVerificationDialogOpen = useSetAtom(isVerificationDialogOpenAtom);
    const { pendingSubmissionAtom, clearPendingSubmission } = usePendingSubmission();
    const setPendingSubmission = useSetAtom(pendingSubmissionAtom);
    const setIsLoading = useSetAtom(isLoadingAtom);
    const setSelectedContexts = useSetAtom(selectedContextsAtom);
    const setSelectedEnvironmentClass = useSetAtom(selectedEnvironmentClassAtom);
    const setContextAssociatedProjects = useSetAtom(contextAssociatedProjectsAtom);
    const { data: organization } = useOrganization();
    const { data: user } = useAuthenticatedUser();
    const api = useGitpodAPI();
    const { submitPrompts } = useSubmitPrompts();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const segmentTrack = useSegmentTrack();
    const store = useStore();
    const navigate = useNavigate();
    const hasSubmittedFromLocation = useRef(false);
    const hasLoadedPendingSubmission = useRef(false);
    const previousOrgId = useRef<string | undefined>(undefined);

    // Parse URL parameters for shareable links
    const [searchParams] = useSearchParams();
    const promptAtom = usePromptAtom();
    const setPrompt = useSetAtom(promptAtom);
    const setIsPrePopulatedPrompt = useSetAtom(isPrePopulatedPromptAtom);

    // Parse URL parameters for shareable links
    const promptParam = searchParams.get("p");

    // Context URL handling for shareable links (hash-based only, requires prompt param)
    const contextUrl = location.hash && promptParam ? location.hash.slice(1) : null;
    const [debouncedContextUrl] = useDebounce(contextUrl, 500);
    const isValidContextUrl = debouncedContextUrl ? isValidURL(debouncedContextUrl) : false;

    const { data: parseContextResponse, isLoading: isLoadingParseContext } = useParseContextURL(
        undefined,
        isValidContextUrl && debouncedContextUrl ? debouncedContextUrl : undefined,
        {
            enabled: isValidContextUrl,
        },
    );

    const { data: projects } = useListProjects();
    const matchedProjects = useMemo<PlainProject[]>(() => {
        if (!parseContextResponse?.projectIds || !projects?.projects) {
            return [];
        }
        return projects.projects.filter((p) => parseContextResponse.projectIds?.includes(p.id)) ?? [];
    }, [parseContextResponse, projects]);

    const isPaymentMethodVerifiedQuery = queryOptions({
        queryKey: ["isPaymentMethodVerified", { organizationId: user?.organizationId }],
        queryFn: async () => {
            try {
                const response = await api.billingService.getBillingInfo({ organizationId: user?.organizationId });
                return response.paymentMethodStatus === PaymentMethodStatus.VERIFIED;
            } catch (error) {
                if (error instanceof ConnectError && error.code === Code.NotFound) {
                    return false;
                }
                throw error;
            }
        },
        enabled: !!user?.organizationId,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });

    const handlePromptSubmission = useCallback(
        async (submission: PromptInputSubmission) => {
            setIsLoading(true);

            try {
                if (!organization) {
                    throw new Error("Organization not available");
                }

                // this will either return the cached billing info or wait for the query to finish fetching and return the result
                const isPaymentMethodVerified = await queryClient.fetchQuery(isPaymentMethodVerifiedQuery);

                if (isPaymentMethodVerified) {
                    await submitPrompts(submission);
                    clearPendingSubmission();
                } else {
                    // remember the submission in-memory for same-session verification flows
                    setPendingSubmission(submission);
                    // cache the submission in local storage to be submitted later in case the user is redirected away to complete verification
                    savePendingSubmissionToLocalStorage(submission, organization.id);
                    segmentTrack("onboarding_verification_required");
                    setIsVerificationDialogOpen(true);
                }
            } catch (error) {
                console.error("failed to submit tasks", error);
                toast({
                    title: "Failed to submit tasks",
                    description: formatError(error),
                });
            } finally {
                setIsLoading(false);
            }
        },
        [
            organization,
            queryClient,
            isPaymentMethodVerifiedQuery,
            submitPrompts,
            setPendingSubmission,
            segmentTrack,
            setIsVerificationDialogOpen,
            toast,
            setIsLoading,
            clearPendingSubmission,
        ],
    );

    const { environmentClasses: environmentClassMap, isLoading: isEnvironmentClassMapLoading } = useEnvironmentClassMap();

    useEffect(() => {
        if (organization?.id !== previousOrgId.current) {
            // Reset prompt input context when switching organizations
            if (previousOrgId.current !== undefined) {
                setSelectedContexts([]);
                setSelectedEnvironmentClass(null);
                setContextAssociatedProjects([]);
            }
            hasLoadedPendingSubmission.current = false;
            previousOrgId.current = organization?.id;
        }
    }, [organization?.id, setSelectedContexts, setSelectedEnvironmentClass, setContextAssociatedProjects]);

    // Auto-save prompt input state (prompt, contexts, environment class) to localStorage
    useAutoSavePromptInputState();

    // Restore stored state from localStorage on mount
    const handleFailedProjects = useCallback(
        (failedProjects: string[]) => {
            toast({
                title: "Some contexts could not be restored",
                description: `The following projects are no longer available: ${failedProjects.join(", ")}`,
            });
        },
        [toast],
    );

    const handleFailedEnvironmentClass = useCallback(
        (environmentClassName: string) => {
            toast({
                title: "Environment class could not be restored",
                description: `The environment class "${environmentClassName}" is no longer available.`,
            });
        },
        [toast],
    );

    useRestoreStoredPromptInputState({
        api,
        queryClient,
        environmentClassMap,
        isEnvironmentClassMapLoading,
        onFailedProjects: handleFailedProjects,
        onFailedEnvironmentClass: handleFailedEnvironmentClass,
    });

    // Handle shareable link prompt parameter
    useEffect(() => {
        if (!isShareableLinksEnabled || !promptParam || !user || !organization) {
            return;
        }

        // Validate prompt length (max 10000 characters)
        const MAX_PROMPT_LENGTH = 10000;
        if (promptParam.length > MAX_PROMPT_LENGTH) {
            toast({
                title: "Prompt too long",
                description: `The shared prompt exceeds the maximum length of ${MAX_PROMPT_LENGTH} characters.`,
            });
            return;
        }

        // Set the prompt text and mark as pre-populated
        setPrompt(promptParam);
        setIsPrePopulatedPrompt(true);

        // Clear URL params using History API (doesn't trigger React re-render)
        window.history.replaceState({}, "", location.pathname);
    }, [isShareableLinksEnabled, promptParam, user, organization, setPrompt, toast, setIsPrePopulatedPrompt, location.pathname]);

    // Track which context we've processed
    const processedContextRef = useRef<string | null>(null);

    // Handle shareable link context parameter (runs after parseContextResponse is available)
    useEffect(() => {
        if (!isShareableLinksEnabled || !parseContextResponse || isLoadingParseContext || !isValidContextUrl || !debouncedContextUrl) {
            return;
        }

        // Skip if we've already processed this exact context
        if (processedContextRef.current === debouncedContextUrl) {
            return;
        }

        processedContextRef.current = debouncedContextUrl;

        // Handle context if provided and parsed successfully
        if (matchedProjects.length > 0) {
            // Use the first matched project
            const project = matchedProjects[0];
            if (project) {
                setSelectedContexts([
                    {
                        kind: "project",
                        project,
                        environmentClasses: [],
                    },
                ]);
            }
        } else if (debouncedContextUrl) {
            // Context URL is valid but no project match - create a repo context
            const url = new URL(debouncedContextUrl);
            const pathParts = url.pathname.split("/").filter(Boolean);

            if (pathParts.length >= 2) {
                const ownerName = pathParts[0];
                const repoName = pathParts[1];
                const branchName = parseContextResponse.git?.branch || "main";

                setSelectedContexts([
                    {
                        kind: "repo",
                        url: debouncedContextUrl,
                        branchName,
                        repoName,
                        ownerName,
                    },
                ]);
            }
        }

        // Clear the hash using History API (doesn't trigger React re-render)
        if (location.hash) {
            window.history.replaceState({}, "", location.pathname);
        }
    }, [
        isShareableLinksEnabled,
        parseContextResponse,
        isLoadingParseContext,
        isValidContextUrl,
        matchedProjects,
        debouncedContextUrl,
        setSelectedContexts,
        location.hash,
        location.pathname,
    ]);

    useEffect(() => {
        if (!organization || hasLoadedPendingSubmission.current) {
            return;
        }

        const pendingSubmission = loadPendingSubmissionFromLocalStorage(organization.id);
        if (!pendingSubmission) {
            return;
        }

        hasLoadedPendingSubmission.current = true;
        setPendingSubmission(pendingSubmission);

        // the state is set when redirected from onboarding flow
        // before the onboarding flow ends, pending submission is stored in local storage
        if (location.state?.submitPendingPrompt && !hasSubmittedFromLocation.current) {
            hasSubmittedFromLocation.current = true;
            void handlePromptSubmission(pendingSubmission);
            // Clear the consumed submission from history state to avoid repeated submissions
            navigate(".", { replace: true, state: null });
        }
    }, [location.state?.submitPendingPrompt, handlePromptSubmission, navigate, setPendingSubmission, organization]);

    if (isLoadingOnaEnabled) {
        return;
    }

    async function handleVerificationSuccess() {
        if (!organization) {
            toast({
                title: "Unable to complete verification",
                description: "Organization not available. Please refresh the page and try again.",
            });
            return;
        }

        setIsVerificationDialogOpen(false);

        let _pendingSubmission: PromptInputSubmission | null = null;
        if (store.get(pendingSubmissionAtom)) {
            _pendingSubmission = store.get(pendingSubmissionAtom);
        } else {
            _pendingSubmission = loadPendingSubmissionFromLocalStorage(organization.id);
            setPendingSubmission(_pendingSubmission);
        }

        if (_pendingSubmission) {
            await submitPrompts(_pendingSubmission);
            clearPendingSubmissionFromLocalStorage(organization.id);
            setPendingSubmission(null);
            clearOnboardingStateFromLocalStorage();
        }

        toast({
            title: "Thank you!",
            description: "We've added $10 in OCUs to your account.",
        });

        setIsLoading(false);
    }

    return (
        <>
            <div data-testid="ona-ai-page" className="mx-auto flex max-w-[877px]">
                {isOnaEnabled ? (
                    <OnaAIContents ref={contentRef} onSubmitPrompt={handlePromptSubmission} />
                ) : (
                    <OnaAINotEnabled />
                )}
            </div>
            <_OnaVerificationDialog onVerificationSuccess={handleVerificationSuccess} />
        </>
    );
};

const _OnaVerificationDialog: FC<{
    onVerificationSuccess: () => void;
}> = ({ onVerificationSuccess }) => {
    const [isVerificationDialogOpen, setIsVerificationDialogOpen] = useAtom(isVerificationDialogOpenAtom);
    const { redirectResult, clearStripePaymentRedirectParams } = useStripePaymentRedirectResult();

    useEffect(() => {
        if (redirectResult?.redirectStatus === "succeeded") {
            setIsVerificationDialogOpen(true);
        }
    }, [redirectResult, setIsVerificationDialogOpen]);

    return (
        <OnaVerificationDialog
            open={isVerificationDialogOpen}
            onOpenChange={setIsVerificationDialogOpen}
            onVerificationSuccess={() => {
                onVerificationSuccess();
                void clearStripePaymentRedirectParams();
            }}
        />
    );
};

const OnaAINotEnabled: FC = () => {
    const { data: user } = useAuthenticatedUser();
    const [greeting, setGreeting] = useState("");

    useEffect(() => {
        const getTimeBasedGreeting = (): string => {
            const hour = new Date().getHours();
            let timeGreeting = "";

            if (hour >= 5 && hour < 12) {
                timeGreeting = "Good Morning";
            } else if (hour >= 12 && hour < 18) {
                timeGreeting = "Good Afternoon";
            } else if (hour >= 18 && hour < 22) {
                timeGreeting = "Good Evening";
            } else {
                timeGreeting = "Good Night";
            }

            const firstName = user?.name ? user.name.split(" ")[0] : undefined;

            return firstName ? `${timeGreeting}; ${firstName}` : timeGreeting;
        };

        setGreeting(getTimeBasedGreeting());
    }, [user]);

    return (
        <div data-testid="ona-ai-page-contents-disabled" className="flex flex-col items-center gap-6">
            <Heading1>{greeting}</Heading1>
            <Text className="text-center text-lg text-content-primary">Ona AI is not yet available to you.</Text>
        </div>
    );
};

const OnaAIContents = forwardRef<
    HTMLDivElement,
    {
        onSubmitPrompt: (submission: PromptInputSubmission) => void;
    }
>(({ onSubmitPrompt }, ref) => {
    const [taskCards, setTaskCards] = useState<TaskCard[]>([]);
    const promptAtom = usePromptAtom();
    const setPrompt = useSetAtom(promptAtom);
    const { pendingSubmissionAtom } = usePendingSubmission();
    const onboardingPendingSubmission = useAtomValue(pendingSubmissionAtom);
    const isPrePopulatedPrompt = useAtomValue(isPrePopulatedPromptAtom);

    const handlePromptClick = useCallback(
        (task: PredefinedTask) => {
            // Insert the prompt text into the prompt box instead of creating a card
            setPrompt(task.prompt);
        },
        [setPrompt],
    );

    const handleTaskCardDismiss = useCallback((taskName: PredefinedTask["taskName"]) => {
        setTaskCards((prev) => prev.filter((card) => card.prompt.taskName !== taskName));
    }, []);

    const handleTaskCardUse = useCallback(
        (prompt: string) => {
            setPrompt(prompt);
        },
        [setPrompt],
    );

    return (
        <div className="flex w-full flex-col items-center justify-center">
            <div
                ref={ref}
                data-testid="ona-ai-page-contents"
                className="flex w-full max-w-[46rem] flex-col gap-12 overflow-x-hidden px-1"
            >
                <div className="flex flex-col items-center gap-6 pt-2 md:gap-4 md:pt-20">
                    <Heading1>
                        <OnaWordmark className="h-8" />
                    </Heading1>

                    <Text className="text-center text-2xl text-content-primary">
                        What do you want to get done today?
                    </Text>
                    {isPrePopulatedPrompt && (
                        <div className="w-full">
                            <Text className="text-center text-sm text-content-secondary">
                                Prompt loaded from shareable link
                            </Text>
                        </div>
                    )}
                    <div className="w-full min-w-full">
                        <NewPromptInput
                            selectedCards={taskCards.map((card) => card.prompt)}
                            onCardDismiss={handleTaskCardDismiss}
                            onCardUse={handleTaskCardUse}
                            onSubmit={onboardingPendingSubmission ? onSubmitPrompt : undefined}
                        />
                    </div>

                    <div
                        className="mt-2 flex w-full flex-row flex-wrap justify-center gap-4"
                        data-testid="example-prompts"
                    >
                        <div className="flex w-full flex-wrap justify-center gap-4 text-sm text-content-secondary">
                            {PREDEFINED_TASKS.map((prompt) => (
                                <PromptTemplate
                                    data-testid={`example-prompt-${prompt.taskName}`}
                                    data-tracking-id={`example-prompt-${prompt.taskName}-ona-ai-page`}
                                    Icon={prompt.Icon}
                                    key={prompt.taskName}
                                    iconColor={prompt.iconColor}
                                    iconBackgroundColor={prompt.iconBackgroundColor}
                                    title={prompt.title}
                                    description={prompt.description}
                                    selected={false}
                                    size="sm"
                                    onSelectChange={() => {
                                        handlePromptClick(prompt);
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <RecentAgentExecutions />
            </div>
        </div>
    );
});
OnaAIContents.displayName = "OnaAIContents";
